import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
    LayoutDashboard, FileText, FileEdit, Users, Truck, Receipt, ScanLine,
    FileCog, Banknote, Scale, BookOpen, Calculator, BarChart3,
    Settings, ShieldCheck, CreditCard, History, ChevronDown, LogOut, Menu, X,
    PanelLeftClose, ChevronsUpDown, Plus, Sparkles, DatabaseZap, BookMarked,
    Compass, Package, Moon, Sun,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import QuickStartGuide from "@/components/QuickStartGuide";
import VerificationBanner from "@/components/VerificationBanner";
import BroadcastBanner from "@/components/BroadcastBanner";
import AssistantWidget from "@/components/AssistantWidget";
import PaymentRequired from "@/pages/PaymentRequired";
import { useTheme } from "@/contexts/ThemeContext";

const NAV = [
    { section: "Overview", items: [
        { to: "/app", end: true, icon: LayoutDashboard, label: "Dashboard" },
    ]},
    { section: "Sales", items: [
        { to: "/app/invoices", icon: FileText, label: "Invoices" },
        { to: "/app/estimates", icon: FileEdit, label: "Estimates" },
        { to: "/app/customers", icon: Users, label: "Customers" },
        { to: "/app/items", icon: Package, label: "Items" },
        { to: "/app/payments", icon: CreditCard, label: "Payments" },
    ]},
    { section: "Purchases", items: [
        { to: "/app/bills", icon: FileCog, label: "Bills" },
        { to: "/app/expenses", icon: Receipt, label: "Expenses" },
        { to: "/app/receipts", icon: ScanLine, label: "Receipts" },
        { to: "/app/vendors", icon: Truck, label: "Vendors" },
        { to: "/app/items", icon: Package, label: "Items" },
        { to: "/app/payments", icon: CreditCard, label: "Payments" },
    ]},
    { section: "Banking", items: [
        { to: "/app/banking", icon: Banknote, label: "Bank Accounts" },
        { to: "/app/reconciliation", icon: Scale, label: "Reconciliation" },
    ]},
    { section: "Accounting", items: [
        { to: "/app/coa", icon: BookOpen, label: "Chart of Accounts" },
        { to: "/app/journal", icon: Calculator, label: "Journal Entries" },
        { to: "/app/reports", icon: BarChart3, label: "Reports" },
    ]},
    { section: "Workspace", items: [
        { to: "/app/team", icon: ShieldCheck, label: "Team & Roles" },
        { to: "/app/data", icon: DatabaseZap, label: "Import / Export" },
        { to: "/app/data-health", icon: Sparkles, label: "Data health" },
        { to: "/app/billing", icon: CreditCard, label: "Subscription" },
        { to: "/app/audit", icon: History, label: "Audit Log" },
        { to: "/app/settings", icon: Settings, label: "Settings" },
        { to: "/app/help", icon: BookMarked, label: "Help Center" },
    ]},
];

function OrgSwitcher() {
    const { orgs, memberships, currentOrg, switchOrg } = useAuth();

    // Split into orgs the user owns/founded vs orgs they were invited to as
    // a team member. The same `memberships` table powers both — the only
    // difference is `role`: "Owner" → owned, anything else → shared.
    const roleByOrg = useMemo(() => {
        const m = {};
        (memberships || []).forEach((mem) => { m[mem.org_id] = mem.role; });
        return m;
    }, [memberships]);

    const owned = orgs.filter((o) => (roleByOrg[o.id] || "Owner") === "Owner");
    const shared = orgs.filter((o) => (roleByOrg[o.id] || "Owner") !== "Owner");

    const renderItem = (o) => (
        <DropdownMenuItem key={o.id} onClick={() => switchOrg(o.id)} data-testid={`org-option-${o.id}`}>
            <span className="flex-1 truncate">{o.name}</span>
            {roleByOrg[o.id] && roleByOrg[o.id] !== "Owner" && (
                <Badge className="bg-sand-200 text-ocean-700 border-0 text-[10px] uppercase tracking-wider mr-1.5">
                    {roleByOrg[o.id]}
                </Badge>
            )}
            {o.id === currentOrg?.id && <Badge className="bg-secondary/10 text-secondary-dark border-0">Active</Badge>}
        </DropdownMenuItem>
    );

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button data-testid="org-switcher" className="w-full flex items-center gap-2 rounded-xl px-3 py-2.5 bg-white border border-sand-300 hover:border-secondary/40 transition-colors text-left">
                    {currentOrg?.logo_url ? (
                        <span className="h-8 w-8 rounded-lg bg-white border border-sand-200 grid place-items-center overflow-hidden">
                            <img src={currentOrg.logo_url} alt="logo" className="h-full w-full object-contain" />
                        </span>
                    ) : (
                        <span className="h-8 w-8 rounded-lg bg-deep-ocean text-white grid place-items-center font-heading font-medium text-sm">
                            {(currentOrg?.name || "B").slice(0, 1).toUpperCase()}
                        </span>
                    )}
                    <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-ocean-700 truncate">{currentOrg?.name || "Select org"}</span>
                        <span className="block text-[11px] text-muted-foreground truncate">
                            {roleByOrg[currentOrg?.id] && roleByOrg[currentOrg?.id] !== "Owner"
                                ? `Shared · ${roleByOrg[currentOrg?.id]}`
                                : currentOrg?.jurisdiction === "KY" ? "Cayman Islands · No-tax" : currentOrg?.jurisdiction || ""}
                        </span>
                    </span>
                    <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
                {owned.length > 0 && (
                    <>
                        <DropdownMenuLabel>Your organizations</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {owned.map(renderItem)}
                    </>
                )}
                {shared.length > 0 && (
                    <>
                        {owned.length > 0 && <DropdownMenuSeparator />}
                        <DropdownMenuLabel className="flex items-center gap-1.5">
                            <Users className="h-3.5 w-3.5 text-secondary-dark" /> Shared with you
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {shared.map(renderItem)}
                    </>
                )}
                {owned.length === 0 && shared.length === 0 && (
                    <DropdownMenuItem disabled>No organizations</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                    <Link to="/onboarding"><Plus className="h-4 w-4 mr-2" /> Create new business</Link>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default function AppShell() {
    const { user, logout, currentOrg } = useAuth();
    const { theme, toggle: toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [quickStartOpen, setQuickStartOpen] = useState(false);
    const [isSuperadmin, setIsSuperadmin] = useState(false);

    useEffect(() => {
        // Best-effort check; fails silently if the endpoint is missing.
        import("@/lib/api").then(({ api }) => {
            api.get("/superadmin/whoami")
                .then(({ data }) => setIsSuperadmin(!!data.is_superadmin))
                .catch(() => setIsSuperadmin(false));
        });
    }, [user?.id]);

    // Scope the .dark class to the app shell: apply while mounted, remove on
    // navigation away to marketing pages (landing, pricing, login, register).
    // useLayoutEffect runs synchronously after each render so the first click
    // on the theme toggle flips the class before paint — no one-click lag.
    useLayoutEffect(() => {
        const root = document.documentElement;
        if (theme === "dark") {
            root.classList.add("dark");
        } else {
            root.classList.remove("dark");
        }
        return () => {
            // Always strip on unmount so /login, /, /pricing stay light-palette
            root.classList.remove("dark");
        };
    }, [theme]);

    // Subscription past-due lockout. Render the full-screen payment-required
    // gate in place of the entire app. The only escape hatches are "retry
    // payment" (fires a fresh PayPal subscription), "refresh after I just
    // paid", and "log out".
    // NOTE: placed AFTER all hooks to keep hook order stable across renders.

    // Auto-open Quick Start for first-time users
    useEffect(() => {
        if (user && user.seen_quickstart === false) {
            const t = setTimeout(() => setQuickStartOpen(true), 600);
            return () => clearTimeout(t);
        }
    }, [user]);

    if (currentOrg?.past_due) {
        return <PaymentRequired />;
    }

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const planBadge = (currentOrg?.plan || "free").toUpperCase();

    return (
        <div className="min-h-screen flex bg-sand-50">
            {/* Sidebar */}
            <aside className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 h-screen w-72 shrink-0 bg-white border-r border-sand-300 transition-transform`}>
                <div className="h-16 flex items-center justify-between px-5 border-b border-sand-200">
                    <Logo />
                    <button onClick={() => setMobileOpen(false)} className="lg:hidden p-2 rounded-lg hover:bg-sand-100">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="px-4 py-4 border-b border-sand-200">
                    <OrgSwitcher />
                </div>
                <nav className="px-3 py-4 overflow-y-auto h-[calc(100vh-9rem)] scrollbar-thin">
                    {NAV.map((sec) => (
                        <div key={sec.section} className="mb-5">
                            <div className="px-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">
                                {sec.section}
                            </div>
                            <ul className="space-y-0.5">
                                {sec.items.map((it) => (
                                    <li key={it.to}>
                                        <NavLink
                                            to={it.to}
                                            end={it.end}
                                            data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                                            className={({ isActive }) =>
                                                `group flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors relative ${
                                                    isActive
                                                        ? "bg-ocean-50 text-ocean-700"
                                                        : "text-muted-foreground hover:bg-sand-100 hover:text-ocean-700"
                                                }`
                                            }
                                        >
                                            {({ isActive }) => (
                                                <>
                                                    <it.icon className={`h-4 w-4 ${isActive ? "text-secondary" : "text-muted-foreground"}`} />
                                                    {it.label}
                                                    {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-coral" />}
                                                </>
                                            )}
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                    {isSuperadmin && (
                        <div className="mb-5">
                            <div className="px-3 text-[10px] uppercase tracking-[0.2em] text-coral-dark font-semibold mb-2">Platform</div>
                            <ul className="space-y-0.5">
                                <li>
                                    <NavLink
                                        to="/app/superadmin"
                                        data-testid="nav-superadmin"
                                        className={({ isActive }) =>
                                            `group flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors relative ${
                                                isActive
                                                    ? "bg-coral/10 text-coral-dark"
                                                    : "text-muted-foreground hover:bg-sand-100 hover:text-coral-dark"
                                            }`
                                        }
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <ShieldCheck className={`h-4 w-4 ${isActive ? "text-coral" : "text-muted-foreground"}`} />
                                                Super admin
                                                {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-coral" />}
                                            </>
                                        )}
                                    </NavLink>
                                </li>
                            </ul>
                        </div>
                    )}
                </nav>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0 flex flex-col">
                {/* Top bar */}
                <header className="sticky top-0 z-30 h-16 glass-panel flex items-center px-5 lg:px-8 gap-4">
                    <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-sand-100" data-testid="mobile-menu-btn">
                        <Menu className="h-5 w-5" />
                    </button>
                    <div className="hidden md:flex items-center gap-2 text-sm">
                        <Badge className="bg-ocean-50 text-ocean-700 border-0 font-medium">{planBadge} PLAN</Badge>
                        {currentOrg?.cayman_no_tax_mode && (
                            <Badge className="bg-coral/10 text-coral-dark border-0 font-medium">Cayman · No-tax mode</Badge>
                        )}
                    </div>
                    <div className="flex-1" />
                    <Link to="/app/billing" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-ocean-700 transition-colors">
                        <Sparkles className="h-3.5 w-3.5" /> Plans
                    </Link>
                    <button onClick={() => setQuickStartOpen(true)} className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-white px-3 py-1.5 text-xs text-ocean-700 hover:border-secondary/40 transition-colors" data-testid="open-quickstart">
                        <Compass className="h-3.5 w-3.5 text-secondary" /> Quick start
                    </button>
                    <button
                        onClick={toggleTheme}
                        className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-sand-300 bg-white text-ocean-700 hover:border-secondary/40 transition-colors"
                        data-testid="theme-toggle"
                        title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                        aria-label="Toggle theme"
                    >
                        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button data-testid="user-menu" className="flex items-center gap-2 rounded-xl pl-2 pr-3 py-1.5 hover:bg-sand-100 transition-colors">
                                <span className="h-8 w-8 rounded-full bg-secondary text-white grid place-items-center text-xs font-semibold">
                                    {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
                                </span>
                                <span className="text-sm font-medium text-ocean-700 hidden sm:inline">{user?.name || user?.email}</span>
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem asChild><Link to="/app/settings">Settings</Link></DropdownMenuItem>
                            <DropdownMenuItem asChild><Link to="/app/billing">Subscription</Link></DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                                <LogOut className="h-4 w-4 mr-2" /> Sign out
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </header>

                <VerificationBanner />
                <BroadcastBanner />

                <main className="flex-1 px-5 lg:px-10 py-8">
                    <Outlet />
                </main>
            </div>

            {mobileOpen && <div onClick={() => setMobileOpen(false)} className="fixed inset-0 z-30 bg-ocean-700/20 lg:hidden" />}

            <QuickStartGuide open={quickStartOpen} onOpenChange={setQuickStartOpen} />
            <AssistantWidget />
        </div>
    );
}
